import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Calendar, CalendarClock, Info, AlertCircle } from 'lucide-react';

/**
 * Reschedule Modal for Operators
 * 
 * Allows Operator/Manager/Admin to reschedule a work order by selecting new dates.
 * When status is RESCHEDULE (tech-requested), shows the technician's reason.
 */
const RescheduleModal = ({
  open,
  onOpenChange,
  workSchedule,
  onReschedule,
  api
}) => {
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setNewStartDate('');
      setNewEndDate('');
      setReason('');
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newStartDate) {
      toast.error('Please select a new start date');
      return;
    }
    
    setSubmitting(true);
    try {
      // Update work schedule with new dates and set status back to SCHEDULED
      const payload = {
        schedule_start_date: newStartDate,
        schedule_end_date: newEndDate || newStartDate,
        status: 'SCHEDULED',
        notes: reason || 'Rescheduled by operator'
      };
      
      await api.put(`/work-schedules/${workSchedule.id}`, payload);
      
      // Format date for toast
      const displayDate = new Date(newStartDate + 'T00:00:00').toLocaleDateString();
      toast.success(`Work order rescheduled to ${displayDate}`);
      
      onOpenChange(false);
      if (onReschedule) onReschedule();
    } catch (error) {
      console.error('Reschedule error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reschedule work order');
    } finally {
      setSubmitting(false);
    }
  };

  if (!workSchedule) return null;

  const currentStartDate = workSchedule.schedule_start_date || workSchedule.schedule_date;
  const currentEndDate = workSchedule.schedule_end_date;
  const techRescheduleReason = workSchedule.reschedule_reason;
  const techRequestedBy = workSchedule.reschedule_requested_by;
  const isRescheduleStatus = workSchedule.status === 'RESCHEDULE';

  // Format dates for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#005EF8]" />
            Reschedule Work Order
          </DialogTitle>
          <DialogDescription>
            {workSchedule.service_request?.car_mark} {workSchedule.service_request?.car_number} — {workSchedule.job_number || workSchedule.service_request?.job_number}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Technician's Reschedule Request (if applicable) */}
          {isRescheduleStatus && techRescheduleReason && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Technician requested reschedule
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    {techRequestedBy && <span className="font-medium">{techRequestedBy}: </span>}
                    {techRescheduleReason}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Current Schedule (Read-only) */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-[#5A5A5A] mb-2">Current Schedule</p>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="font-medium">
                {formatDate(currentStartDate)}
                {currentEndDate && currentEndDate !== currentStartDate && (
                  <> — {formatDate(currentEndDate)}</>
                )}
              </span>
            </div>
          </div>

          {/* New Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="newStartDate">New Start Date *</Label>
              <Input
                type="date"
                id="newStartDate"
                value={newStartDate}
                onChange={(e) => setNewStartDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
                data-testid="reschedule-start-date"
              />
            </div>
            <div>
              <Label htmlFor="newEndDate">New End Date</Label>
              <Input
                type="date"
                id="newEndDate"
                value={newEndDate}
                onChange={(e) => setNewEndDate(e.target.value)}
                min={newStartDate || new Date().toISOString().split('T')[0]}
                data-testid="reschedule-end-date"
              />
              <p className="text-xs text-[#5A5A5A] mt-1">Leave blank for single-day</p>
            </div>
          </div>

          {/* Reason (Optional for operators) */}
          <div>
            <Label htmlFor="reason">Reason for Reschedule (Optional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Customer requested delay, parts not available..."
              rows={2}
              data-testid="reschedule-reason"
            />
          </div>

          {/* Info about status change */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />
              This will update the work order dates and set status back to "Scheduled"
            </p>
          </div>
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!newStartDate || submitting}
            className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
            data-testid="confirm-reschedule-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Rescheduling...
              </>
            ) : (
              <>
                <CalendarClock className="w-4 h-4 mr-2" />
                Reschedule
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleModal;
